// Knowledge Center articles — factual, technical content for SEO + AI citation.
// Each article reflects that Crimson Inks manufactures both INKS and COATINGS.

export const articles = [
  {
    id: 1,
    slug: "uv-vs-led-curing-for-printing",
    category: "Curing Technology",
    readTime: "7 min read",
    title: "UV vs LED Curing for Printing: Differences, Benefits & When to Use Each",
    metaTitle: "UV vs LED Curing for Printing – Differences & Benefits | Crimson Inks",
    metaDescription:
      "UV vs LED curing explained: how each technology works, energy and heat differences, costs, and when to use UV or LED curable inks and coatings.",
    excerpt:
      "Both UV and LED curing harden inks and coatings with light instead of heat or solvents — but they differ in lamp type, energy use, heat and cost. Here is how to choose.",
    heroIntro:
      "UV and LED curing both use ultraviolet light to instantly harden inks and coatings, but they differ in lamp type, energy use, heat output and cost. Conventional UV uses broad-spectrum mercury lamps, while LED uses narrow-band diodes (typically 365–405 nm) that run cooler, last far longer and consume less power.",
    sections: [
      {
        heading: "What 'curing' actually means",
        body: [
          "Curing is the process of turning a liquid ink or coating into a solid film. Unlike conventional inks that dry by evaporating solvent or water, UV and LED curable inks and coatings contain photoinitiators that react the instant they are exposed to ultraviolet light.",
          "This reaction, called photopolymerization, links the resins together into a hard, durable film in a fraction of a second. Because there is no solvent to drive off, the film is effectively 100% of what you applied — which is why these systems are described as high-solids or 100%-solids.",
        ],
      },
      {
        heading: "How conventional UV curing works",
        body: [
          "UV curing uses medium-pressure mercury (or doped gallium/iron) lamps that emit a broad spectrum of ultraviolet light, including UVA, UVB and UVC. This wide spectrum cures a broad range of formulations and surface chemistries.",
          "The trade-offs are that mercury lamps need warm-up time, run very hot, emit ozone, contain mercury, and have a shorter service life. The extra heat can distort thin or heat-sensitive films.",
        ],
      },
      {
        heading: "How LED curing works",
        body: [
          "LED curing uses solid-state diodes that emit a single narrow wavelength. They switch on and off instantly, generate very little infrared heat, contain no mercury, produce no ozone, and last 20,000 hours or more.",
          "The narrow output means the ink or coating must contain photoinitiators tuned to that exact wavelength — which is why LED-curable formulations are designed specifically for LED lamps.",
        ],
      },
      {
        heading: "UV vs LED at a glance",
        table: {
          headers: ["Factor", "Conventional UV", "LED"],
          rows: [
            ["Light source", "Mercury / doped lamps", "Solid-state diodes"],
            ["Spectrum", "Broad (UVA/B/C)", "Narrow (365–405 nm)"],
            ["Warm-up", "Required", "Instant on/off"],
            ["Heat on substrate", "High", "Low"],
            ["Lamp life", "~1,000–2,000 hrs", "20,000+ hrs"],
            ["Energy use", "Higher", "Lower"],
            ["Ozone / mercury", "Yes", "None"],
            ["Upfront cost", "Lower", "Higher"],
          ],
        },
      },
      {
        heading: "When to choose UV",
        body: ["Conventional UV remains a strong choice when:"],
        list: [
          "You print on heat-tolerant substrates such as paper, board and rigid stock",
          "You need a broad-spectrum cure for varied or specialty chemistries",
          "Upfront equipment budget is the primary constraint",
        ],
      },
      {
        heading: "When to choose LED",
        body: ["LED curing is usually preferred when:"],
        list: [
          "You run heat-sensitive films like thin polyester, PVC or shrink materials",
          "Energy efficiency and lower running costs are a priority",
          "You want instant start-up, less downtime and longer lamp life",
          "You are working toward lower emissions and a smaller environmental footprint",
        ],
      },
      {
        heading: "Do you need different inks and coatings?",
        body: [
          "Yes. UV inks and coatings will not fully cure under an LED lamp, and LED-curable products are optimised for narrow-band output. Switching curing technology means switching to a matched ink or coating formulation.",
          "Crimson Inks manufactures both UV and LED curable inks and coatings, so a press can be matched to either technology — or migrated from UV to LED — without compromising adhesion, gloss or cure speed.",
        ],
      },
    ],
    faqs: [
      {
        question: "Can I run UV inks under an LED lamp?",
        answer:
          "Generally no. UV inks are formulated for broad-spectrum mercury lamps and will not cure properly under a narrow-band LED lamp. You need LED-curable inks and coatings designed for the LED wavelength.",
      },
      {
        question: "Is LED curing better than UV?",
        answer:
          "It depends on the application. LED is better for energy efficiency, heat-sensitive films and long lamp life, while conventional UV can be preferable for heat-tolerant substrates and certain broad-spectrum chemistries.",
      },
      {
        question: "Does light curing dry the ink instantly?",
        answer:
          "Yes. Both UV and LED curable inks and coatings cure within a fraction of a second of exposure to the correct ultraviolet light, allowing immediate handling and finishing.",
      },
    ],
    crimsonPerspective:
      "Crimson Inks develops both UV and LED curable inks and coatings for gravure, screen, offset and flexo printing. Our technical team can recommend the right system for your substrate and lamp type, and support presses migrating from conventional UV to LED.",
  },

  {
    id: 2,
    slug: "reduce-energy-costs-vocs-uv-led",
    category: "Sustainability",
    readTime: "6 min read",
    title: "How UV & LED Curable Inks and Coatings Cut Energy Costs and VOCs",
    metaTitle: "How UV & LED Curable Inks Reduce Energy Costs and VOCs | Crimson Inks",
    metaDescription:
      "Discover how UV and LED curable inks and coatings lower energy consumption and VOC emissions compared with solvent- and water-based systems.",
    excerpt:
      "Because they cure with light instead of heat or solvent evaporation, UV and LED inks and coatings can dramatically reduce both energy use and VOC emissions.",
    heroIntro:
      "UV and LED curable inks and coatings cure by light rather than by heat or solvent evaporation, so they contain little to no VOCs and use far less energy than conventional systems. LED curing in particular can reduce curing energy by 50–75% versus heat-dried processes.",
    sections: [
      {
        heading: "Why conventional systems use so much energy",
        body: [
          "Solvent- and water-based inks and coatings dry by evaporation. That requires long drying ovens, large volumes of heated air and significant electricity or gas to remove the carrier from the film.",
          "Much of that energy is spent simply heating air and the substrate, not on the printed film itself — making the process inherently inefficient.",
        ],
      },
      {
        heading: "How light-curing changes the equation",
        body: [
          "UV and LED systems cure the film in a fraction of a second as it passes under a lamp. There are no long ovens, no large heated-air systems and far less wasted energy.",
          "This also shrinks the press footprint and reduces the load on factory ventilation and air-conditioning.",
        ],
      },
      {
        heading: "VOCs: what they are and why they matter",
        body: [
          "Volatile organic compounds (VOCs) are solvents that evaporate during drying. They contribute to air pollution, raise workplace exposure concerns and are increasingly regulated.",
          "Because UV and LED curable inks and coatings are essentially 100% solids, they release minimal or no VOCs — helping printers meet emission norms and improve workplace air quality.",
        ],
      },
      {
        heading: "Where the energy savings come from with LED",
        body: ["LED curing adds further savings on top of the light-cure advantage:"],
        list: [
          "No warm-up or cool-down — lamps switch on and off instantly",
          "Very low infrared heat, reducing cooling demand",
          "Lamp life of 20,000+ hours, cutting replacement costs",
          "No ozone extraction system required",
          "Lower standby power between jobs",
        ],
      },
      {
        heading: "Other operational benefits",
        body: ["Beyond energy and emissions, switching often improves productivity:"],
        list: [
          "Faster throughput — prints are cured and ready immediately",
          "Less spoilage from set-off, blocking or solvent retention",
          "Consistent, durable films with strong chemical and rub resistance",
          "Easier compliance with environmental regulations",
        ],
      },
    ],
    faqs: [
      {
        question: "Are UV and LED inks really low-VOC?",
        answer:
          "Yes. UV and LED curable inks and coatings are high-solids systems that cure by light, so they emit little to no volatile organic compounds compared with solvent-based products.",
      },
      {
        question: "Will switching to LED reduce my electricity bill?",
        answer:
          "In most cases, yes. LED curing eliminates warm-up time, runs cool, uses less power and reduces cooling and extraction loads, which together can significantly lower curing energy costs.",
      },
      {
        question: "Are these systems safer for workers and the environment?",
        answer:
          "They reduce solvent emissions and, in the case of LED, eliminate mercury and ozone. With correct handling, they support a cleaner workplace and easier regulatory compliance.",
      },
    ],
    crimsonPerspective:
      "Crimson Inks formulates low-VOC UV and LED curable inks and coatings that help printers cut energy use and emissions while maintaining print quality. We can advise on the right system for your sustainability and compliance goals.",
  },

  {
    id: 3,
    slug: "choosing-ink-coating-metallised-polyester-pvc-polycarbonate",
    category: "Application Guide",
    readTime: "8 min read",
    title: "Choosing the Right Ink or Coating for Metallised Polyester, PVC & Polycarbonate",
    metaTitle: "Best Ink & Coating for Metallised Polyester, PVC, Polycarbonate | Crimson Inks",
    metaDescription:
      "How to select the right ink or coating for metallised polyester, PVC and polycarbonate — surface energy, adhesion, flexibility and curing tips.",
    excerpt:
      "Each substrate behaves differently. Matching ink and coating chemistry to metallised polyester, PVC or polycarbonate prevents adhesion failure, cracking and poor finish.",
    heroIntro:
      "Choosing the right ink or coating for metallised polyester, PVC and polycarbonate comes down to surface energy, adhesion chemistry, flexibility and curing method. Using a formulation matched to the material prevents the most common failures: peeling, cracking and poor gloss.",
    sections: [
      {
        heading: "Why the substrate matters",
        body: [
          "Inks and coatings must wet out and bond to the surface they are printed on. Low-surface-energy materials resist wetting, while some plastics react poorly to certain solvents or to heat during curing.",
          "Surface energy (measured in dynes), surface treatment such as corona, and the chemistry of the binder all determine whether the film will adhere and stay adhered through converting and end use.",
        ],
      },
      {
        heading: "Metallised polyester",
        body: [
          "Metallised films have a thin metal layer that gives a low-energy surface and is sensitive to heat. The challenge is bonding to the metallised side without lifting or dulling it.",
        ],
        list: [
          "Use high-adhesion, flexible formulations designed for metallised surfaces",
          "Favour LED curing or controlled drying to limit heat on the film",
          "Verify corona treatment and dyne level before printing",
          "Test adhesion to the metal layer, not just the base film",
        ],
      },
      {
        heading: "PVC (rigid and flexible)",
        body: [
          "PVC is versatile but flexible grades contain plasticizers that can migrate over time and soften or attack a rigid ink film, causing it to crack or lose adhesion.",
        ],
        list: [
          "Choose flexible binders for flexible PVC to avoid cracking",
          "Confirm plasticizer compatibility for long-term durability",
          "Ensure good wetting for solid, even coverage",
        ],
      },
      {
        heading: "Polycarbonate sheet",
        body: [
          "Polycarbonate offers excellent clarity and impact resistance but is prone to stress cracking when exposed to aggressive solvents.",
        ],
        list: [
          "Use solvent-compatible or 100%-solids UV/LED systems to avoid stress cracking",
          "Prioritise clarity-preserving, non-yellowing coatings for transparent parts",
          "Balance adhesion with the scratch and chemical resistance the end use needs",
        ],
      },
      {
        heading: "Quick selection guide",
        table: {
          headers: ["Substrate", "Key challenge", "What to look for", "Curing tip"],
          rows: [
            ["Metallised polyester", "Low energy, heat-sensitive", "High-adhesion, flexible", "Prefer LED / low heat"],
            ["PVC (flexible)", "Plasticizer migration", "Flexible, compatible binder", "Avoid over-drying"],
            ["PVC (rigid)", "Good wetting", "Strong adhesion system", "Standard UV/LED"],
            ["Polycarbonate", "Stress cracking, clarity", "Solvent-safe, non-yellowing", "100%-solids UV/LED"],
          ],
        },
      },
      {
        heading: "Always test before production",
        body: [
          "Whatever the substrate, validate with a tape or cross-hatch adhesion test, a flexibility (crease) test, a rub test and a check against the real end-use conditions such as lamination, slitting or outdoor exposure.",
        ],
      },
    ],
    faqs: [
      {
        question: "Why is ink peeling off my metallised film?",
        answer:
          "Peeling usually points to low surface energy, inadequate corona treatment, contamination, or an ink not designed for the metallised surface. A high-adhesion, flexible formulation matched to the film typically resolves it.",
      },
      {
        question: "Can one ink work on all three substrates?",
        answer:
          "Sometimes, but it is rarely optimal. Metallised polyester, PVC and polycarbonate have different surface and flexibility demands, so substrate-specific or custom formulations give more reliable adhesion and durability.",
      },
      {
        question: "Do I need surface treatment before printing?",
        answer:
          "Often yes. Many films require corona or plasma treatment to raise surface energy to the dyne level needed for good wetting and adhesion. Always verify the dyne level before printing.",
      },
    ],
    crimsonPerspective:
      "Crimson Inks creates custom-formulated inks and coatings matched to specific substrates including metallised polyester, PVC, polycarbonate, paper and metal sheets. Use our Product Finder to shortlist options, or contact our team for a tailored recommendation.",
    relatedLinks: [
      { label: "Try the Product Finder", to: "/#product-finder" },
      { label: "Fix poor adhesion on substrate", to: "/gravure-troubleshooting/poor-adhesion-substrate" },
    ],
  },

  {
    id: 4,
    slug: "uv-led-inks-coatings-gravure-screen-offset-flexo",
    category: "Application Guide",
    readTime: "8 min read",
    title: "UV & LED Inks and Coatings for Gravure, Screen, Offset & Flexo: A Comparison",
    metaTitle: "UV & LED Inks and Coatings for Gravure, Screen, Offset, Flexo | Crimson Inks",
    metaDescription:
      "Compare UV and LED inks and coatings across gravure, screen, offset and flexo printing — viscosity, film thickness and the right formulation for each process.",
    excerpt:
      "Each printing process transfers ink differently, so the ideal UV/LED ink or coating differs in viscosity, film thickness and rheology. Here is how they compare.",
    heroIntro:
      "Gravure, screen, offset and flexo each transfer ink through a different mechanism — engraved cylinders, mesh stencils, rubber blankets or flexible plates — so the ideal UV and LED ink or coating differs in viscosity, film weight and rheology for each process.",
    sections: [
      {
        heading: "Why the printing process dictates the ink",
        body: [
          "The way ink is metered and transferred sets the viscosity and film thickness a formulation must have. A coating that runs perfectly on a gravure cylinder would be far too thin for a screen press, and an offset paste ink would never flow through an anilox.",
          "Matching the ink or coating to the process is the difference between clean, consistent printing and constant press-side problems.",
        ],
      },
      {
        heading: "Gravure",
        body: [
          "Gravure transfers ink from cells engraved into a cylinder, so inks are low in viscosity and capable of very fine detail at high speed. It is widely used for flexible packaging and high-volume film printing.",
          "UV and LED gravure inks and coatings deliver instant cure with low solvent retention, supporting fast, clean runs on films.",
        ],
      },
      {
        heading: "Screen",
        body: [
          "Screen printing pushes ink through a mesh stencil, laying down a thick, high-opacity film. It is ideal for bold solids, tactile effects and high-build spot coatings.",
          "UV and LED screen inks and coatings cure these heavy films quickly and are popular for raised, textured and high-gloss spot finishes.",
        ],
      },
      {
        heading: "Offset",
        body: [
          "Offset uses high-viscosity paste inks transferred from plate to blanket to substrate, mainly on paper and board. UV and LED offset inks cure instantly for fast finishing.",
          "Overprint varnishes (OPV) — gloss, matte or soft-touch — are widely applied inline or offline to protect and enhance the print.",
        ],
      },
      {
        heading: "Flexo",
        body: [
          "Flexo prints from a flexible relief plate fed by an anilox roller, using low-to-medium viscosity inks. It is a workhorse for labels and flexible packaging.",
          "UV and LED flexo inks and coatings combine fast cure with strong adhesion on films and label stocks.",
        ],
      },
      {
        heading: "Process comparison",
        table: {
          headers: ["Process", "Ink type / viscosity", "Typical film", "Common UV/LED use"],
          rows: [
            ["Gravure", "Low viscosity", "Thin", "Flexible packaging, films"],
            ["Screen", "High build / paste", "Thick", "Solids, tactile, spot UV"],
            ["Offset", "High-viscosity paste", "Thin", "Cartons, OPV varnishes"],
            ["Flexo", "Low–medium viscosity", "Thin–medium", "Labels, flexible packaging"],
          ],
        },
      },
      {
        heading: "Coatings and varnishes across processes",
        body: [
          "Beyond colour, UV and LED coatings add gloss, matte, soft-touch, high-build and security effects. The same finish often needs a different formulation depending on whether it is applied by gravure, screen, offset or flexo.",
        ],
      },
    ],
    faqs: [
      {
        question: "Is UV ink the same for all printing processes?",
        answer:
          "No. Gravure, screen, offset and flexo require different viscosities and film weights, so UV and LED inks and coatings are formulated specifically for each process.",
      },
      {
        question: "Which process is best for flexible packaging films?",
        answer:
          "Gravure and flexo are the most common choices for flexible packaging, with UV and LED systems offering fast cure and low solvent retention on films.",
      },
      {
        question: "Can I achieve a high-gloss finish?",
        answer:
          "Yes. UV and LED overprint varnishes and high-build screen coatings produce strong gloss, matte and tactile finishes depending on the process and formulation.",
      },
    ],
    crimsonPerspective:
      "Crimson Inks manufactures UV and LED curable inks and coatings engineered specifically for gravure, screen, offset and flexo printing — including overprint varnishes and specialty finishes — so each press gets a formulation matched to its process.",
    relatedLinks: [{ label: "Explore the Product Finder", to: "/#product-finder" }],
  },

  {
    id: 5,
    slug: "choosing-uv-led-ink-coating-manufacturer-india",
    category: "Buyer's Guide",
    readTime: "7 min read",
    title: "What to Look For When Choosing a UV & LED Ink and Coating Manufacturer in India",
    metaTitle: "Choosing a UV & LED Ink and Coating Manufacturer in India | Crimson Inks",
    metaDescription:
      "A practical buyer's guide to selecting a UV and LED ink and coating manufacturer in India — certification, custom formulation, technical support and reliability.",
    excerpt:
      "Price is only part of the story. The right manufacturer brings formulation expertise, certification, custom development and real technical support.",
    heroIntro:
      "When choosing a UV and LED ink and coating manufacturer in India, look beyond price: evaluate formulation expertise, quality certification, the range of processes and substrates supported, custom development capability, technical support and batch-to-batch consistency.",
    sections: [
      {
        heading: "1. A full range of inks and coatings",
        body: [
          "A capable manufacturer supplies both inks and coatings across multiple processes — gravure, screen, offset and flexo — and a wide set of substrates. Sourcing both from one partner simplifies supply, support and quality control.",
        ],
      },
      {
        heading: "2. Quality and certification",
        body: [
          "Look for ISO 9001:2015 certification and a documented quality system. Consistent, repeatable batches matter as much as the certificate itself, because shade and performance drift causes costly rework on press.",
        ],
      },
      {
        heading: "3. Custom formulation capability",
        body: [
          "Off-the-shelf products do not suit every substrate, press or end use. A manufacturer with genuine R&D can tailor adhesion, flexibility, gloss, cure speed and resistance to your exact requirement.",
        ],
      },
      {
        heading: "4. Technical support",
        body: [
          "The best suppliers help you solve press-side problems — streaking, adhesion, drying, gloss — not just sell product. Access to ink technologists and a practical knowledge base shortens downtime.",
        ],
      },
      {
        heading: "5. Compliance and sustainability",
        body: [
          "Low-VOC UV and LED systems help you meet environmental norms and improve workplace safety. A forward-looking manufacturer supports your compliance and sustainability goals.",
        ],
      },
      {
        heading: "6. Reliability and supply",
        body: [
          "Dependable lead times, stable pricing and consistent quality keep your production running. Evaluate a supplier's track record and responsiveness, not just the quote.",
        ],
      },
      {
        heading: "Buyer's checklist",
        body: ["Before you commit, confirm the manufacturer offers:"],
        list: [
          "Both UV and LED inks and coatings across your processes",
          "ISO 9001:2015 certification and batch consistency",
          "Custom formulation and R&D capability",
          "Substrate-specific recommendations and samples for testing",
          "Responsive technical and press-side support",
          "Low-VOC, compliant formulations",
        ],
      },
      {
        heading: "Why a local Indian manufacturer can be an advantage",
        body: [
          "A domestic manufacturer can offer faster technical support, shorter lead times, lower logistics cost and a better understanding of local press and climate conditions — often with the same or better formulation quality than imported products.",
        ],
      },
    ],
    faqs: [
      {
        question: "What certifications should an ink and coating manufacturer have?",
        answer:
          "ISO 9001:2015 is the key quality-management certification to look for, backed by a documented quality system and consistent batch-to-batch performance.",
      },
      {
        question: "Should I choose a local or international supplier?",
        answer:
          "A capable local manufacturer often delivers faster support, shorter lead times and lower logistics cost while matching imported quality — a strong advantage for day-to-day production in India.",
      },
      {
        question: "Can manufacturers create custom formulations?",
        answer:
          "Yes. Manufacturers with in-house R&D can tailor inks and coatings to your specific substrate, press and end-use requirements for adhesion, flexibility, gloss and cure speed.",
      },
    ],
    crimsonPerspective:
      "Crimson Inks Private Limited is an ISO 9001:2015 certified manufacturer of UV and LED curable inks and coatings, serving printers across India since 2000 from Kanpur. We combine custom formulation, both inks and coatings across all major processes, and hands-on technical support.",
    relatedLinks: [{ label: "Contact our team", to: "/#contact" }],
  },
];

export const getArticleBySlug = (slug) => articles.find((a) => a.slug === slug);
export const getAllArticles = () => articles;
